import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, Loader2, TrendingDown, RefreshCw } from "lucide-react";
import { TestWelcomeEmailButton } from "@/components/TestWelcomeEmailButton";
import { DncBypassCard } from "@/components/DncBypassCard";
import { AttomTestCard } from "@/components/AttomTestCard";
import { KeyHealthCard } from "@/components/KeyHealthCard";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rate, setRate] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  useEffect(() => { api.get("/admin/market-rate").then((r) => setRate(r.data)).catch(() => {}); }, []);
  if (!s) return null;

  const refreshRate = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/market-rate/refresh");
      setRate(data);
      toast.success(`Market rate updated to ${Number(data.rate).toFixed(2)}%`);
    } catch (e) { toast.error(apiError(e)); }
    setRefreshing(false);
  };

  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const setCheck = (k, v) => setS((x) => ({ ...x, compliance_checks: { ...x.compliance_checks, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        prospecting_run_price: parseFloat(s.prospecting_run_price),
        bulk_outreach_price: parseFloat(s.bulk_outreach_price),
        deity_mode_price: parseFloat(s.deity_mode_price),
        deity_max_unanswered_days: parseInt(s.deity_max_unanswered_days ?? 7),
        individual_contact_price: parseFloat(s.individual_contact_price),
        prospects_per_run: parseInt(s.prospects_per_run),
        skip_trace_max_per_run: parseInt(s.skip_trace_max_per_run),
        max_properties_checked_per_run: parseInt(s.max_properties_checked_per_run ?? 0) || 0,
        reverse_age_lookup_enabled: s.reverse_age_lookup_enabled,
        voicedrop_sender_number: s.voicedrop_sender_number,
        email_outreach_enabled: s.email_outreach_enabled,
        voicemail_outreach_enabled: s.voicemail_outreach_enabled,
        duplicate_voicemail_to_mlo: s.duplicate_voicemail_to_mlo,
        cc_mlo_on_emails: s.cc_mlo_on_emails,
        auto_voicemail_new_leads: s.auto_voicemail_new_leads,
        marketplace_enabled: !!s.marketplace_enabled,
        marketplace_auto_call: !!s.marketplace_auto_call,
        marketplace_admin_call: !!s.marketplace_admin_call,
        sms_enabled: !!s.sms_enabled,
        postcards_enabled: s.postcards_enabled !== false,
        deity_texting: s.deity_texting !== false,
        data_refresh_price_per_lead: parseFloat(s.data_refresh_price_per_lead ?? 0.25),
        marketplace_price: parseFloat(s.marketplace_price ?? 15),
        lt_return_price: parseFloat(s.lt_return_price ?? 20),
        auto_email_new_leads: s.auto_email_new_leads,
        lead_release_enabled: s.lead_release_enabled,
        lead_release_days: parseInt(s.lead_release_days),
        require_signup_approval: s.require_signup_approval,
        app_url: s.app_url,
        available_states: typeof s.available_states === "string"
          ? s.available_states.split(",").map((x) => x.trim()).filter(Boolean) : s.available_states,
        compliance_checks: s.compliance_checks,
        qualification_criteria: s.qualification_criteria,
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  const Toggle = ({ k, label, desc }) => (
    <div className="flex items-center justify-between gap-4 border-b border-slate-100 py-3">
      <div><div className="text-sm font-semibold text-slate-800">{label}</div><div className="text-xs text-slate-500">{desc}</div></div>
      <Switch checked={!!s[k]} onCheckedChange={(v) => set(k, v)} data-testid={`toggle-${k}`} />
    </div>
  );

  return (
    <div className="max-w-3xl">
      <PageHeader title="Settings" subtitle="Configure pricing, outreach and compliance.">
        <Button onClick={save} disabled={saving} data-testid="save-settings-btn" className="gold-cta border-0 font-bold">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Save Changes</>}
        </Button>
      </PageHeader>

      <Card className="mb-5 p-6" data-testid="postcards-settings-card">
        <div className="flex items-center justify-between gap-4">
          <div><h3 className="font-display font-bold text-slate-900">Postcards</h3>
            <p className="text-xs text-slate-500">Master switch for every account: MLO, property manager and surety. When off, no postcards are mailed (manual or autopilot); queued postcards wait until you turn it back on. Click Save Changes to apply.</p></div>
          <Switch checked={s.postcards_enabled !== false} onCheckedChange={(v) => set("postcards_enabled", v)} data-testid="toggle-postcards_enabled" />
        </div>
      </Card>

      <Card className="mb-5 p-6" data-testid="marketplace-settings-card">
        <h3 className="mb-2 font-display font-bold text-slate-900">Lead Marketplace</h3>
        <Toggle k="marketplace_enabled" label="Marketplace open" desc="Lets MLOs you've given Marketplace access (in MLO Accounts) browse and buy inventory leads." />
        <Toggle k="marketplace_admin_call" label="Call me when a new lead is listed" desc="Ava phones you (the number on your My Profile) each time a new lead is added to the Marketplace." />
        <Toggle k="marketplace_auto_call" label="Auto-call new leads (Marketplace purchases & MortgageLeads.com / Astoria)" desc="When an MLO buys a Marketplace lead, or a lead arrives from MortgageLeads.com (Astoria) through Zapier, Ava calls the MLO to say she's about to call the lead, calls the lead and live-transfers, then calls the MLO back with the result: appointment scheduled, not interested, hung up or didn't answer. Leads outside 8 AM to 8 PM are announced right away and called at 8 AM their time. Ava never calls the same lead more than once a day." />
        <div className="mt-3 max-w-xs"><Label>Price per lead (USD)</Label>
          <div className="relative mt-1.5"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
            <Input type="number" step="0.01" className="pl-7" value={s.marketplace_price ?? 15} onChange={(e) => set("marketplace_price", e.target.value)} data-testid="setting-marketplace-price" /></div>
          <p className="mt-1 text-xs text-slate-400">Applies to leads added from now on.</p></div>
        <div className="mt-3 max-w-xs"><Label>Price per returned / released live transfer lead (USD)</Label>
          <div className="relative mt-1.5"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
            <Input type="number" step="0.01" min="0" className="pl-7" value={s.lt_return_price ?? 20} onChange={(e) => set("lt_return_price", e.target.value)} data-testid="setting-lt-return-price" /></div>
          <p className="mt-1 text-xs text-slate-400">Live transfer leads sent back to the Marketplace (after a week, or released by the loan officer). Applies from now on; change any single lead's price in Lead Marketplace.</p></div>
      </Card>

      <Card className="mb-5 p-6">
        <h3 className="mb-4 font-display font-bold text-slate-900">Pricing & Volume</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div><Label>Prospecting-run price (USD)</Label>
            <div className="relative mt-1.5"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
              <Input type="number" step="0.01" className="pl-7" value={s.prospecting_run_price} onChange={(e) => set("prospecting_run_price", e.target.value)} data-testid="setting-price" /></div>
          </div>
          <div><Label>Max prospects per run</Label><Input type="number" className="mt-1.5" value={s.prospects_per_run} onChange={(e) => set("prospects_per_run", e.target.value)} data-testid="setting-prospects-per-run" /></div>
          <div><Label>Bulk "Contact all" price (USD)</Label>
            <div className="relative mt-1.5"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
              <Input type="number" step="0.01" className="pl-7" value={s.bulk_outreach_price} onChange={(e) => set("bulk_outreach_price", e.target.value)} data-testid="setting-bulk-price" /></div>
            <p className="mt-1 text-xs text-slate-400">One-click email and postcard to an MLO's whole list, plus AI calls to their imported, inbound and Interested leads.</p>
          </div>
          <div><Label>Individual contact price (USD)</Label>
            <div className="relative mt-1.5"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
              <Input type="number" step="0.01" className="pl-7" value={s.individual_contact_price ?? 1} onChange={(e) => set("individual_contact_price", e.target.value)} data-testid="setting-contact-price" /></div>
            <p className="mt-1 text-xs text-slate-400">Charged per single AI call or postcard an MLO sends from a lead's panel. Admins are never charged. Under $0.50 is treated as free.</p>
          </div>
          <div><Label>AI Autopilot Mode price per month (USD)</Label>
            <div className="relative mt-1.5"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
              <Input type="number" step="0.01" className="pl-7" value={s.deity_mode_price ?? 1000} onChange={(e) => set("deity_mode_price", e.target.value)} data-testid="setting-deity-price" /></div>
            <p className="mt-1 text-xs text-slate-400">Monthly Stripe subscription that renews automatically while an MLO has AI Autopilot Mode on. Override per MLO in MLO Accounts.</p>
          </div>
          <div><Label>AI Autopilot Mode: stop after unanswered days</Label><Input type="number" min="0" className="mt-1.5" value={s.deity_max_unanswered_days ?? 7} onChange={(e) => set("deity_max_unanswered_days", e.target.value)} data-testid="setting-deity-max-unanswered" /><p className="mt-1 text-xs text-slate-400">AI Autopilot Mode stops calling a lead after this many days of no answer, voicemail or failed calls. 0 = no limit.</p></div>
          <div><Label>Max skip-traces per run</Label><Input type="number" className="mt-1.5" value={s.skip_trace_max_per_run} onChange={(e) => set("skip_trace_max_per_run", e.target.value)} data-testid="setting-skip-trace-cap" /><p className="mt-1 text-xs text-slate-400">Caps AvaData phone/email lookups per run. 0 = no limit (runs keep looking up homes until they have enough leads with a phone and email).</p></div>
          <div><Label>Max properties searched per run</Label><Input type="number" min="0" className="mt-1.5" value={s.max_properties_checked_per_run ?? 0} onChange={(e) => set("max_properties_checked_per_run", e.target.value)} data-testid="setting-max-properties-checked" /><p className="mt-1 text-xs text-slate-400">Runs keep searching properties until they reach the lead target. 0 = no limit. Each property checked uses property-data and AI scoring.</p></div>
        </div>
        <div className="mt-2">
          <Toggle k="reverse_age_lookup_enabled" label="Reverse-mortgage age lookup (62+)" desc="Uses AvaData deep demographic search to append homeowner age, only on Reverse Mortgage runs, so anyone 62+ is sourced as a Reverse Mortgage lead. Other runs use the cheaper phone/email lookup. Costs more skip-trace credits per lookup." />
        </div>
      </Card>

      <Card className="mb-5 p-6" data-testid="market-rate-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="mb-1 font-display font-bold text-slate-900">Current Market Rate</h3>
            <p className="text-xs text-slate-500">Live 30-yr fixed rate from Freddie Mac (FRED). Refinance leads are only sourced when a homeowner's inferred rate is above this.</p>
          </div>
          <Button variant="outline" onClick={refreshRate} disabled={refreshing} data-testid="refresh-rate-btn">
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="mr-2 h-4 w-4" /> Refresh</>}
          </Button>
        </div>
        <div className="mt-4 flex items-end gap-3">
          <TrendingDown className="mb-1 h-6 w-6 text-emerald-600" />
          <span className="font-display text-4xl font-extrabold text-slate-900" data-testid="market-rate-display">
            {rate ? `${Number(rate.rate).toFixed(2)}%` : "—"}
          </span>
          {rate?.date && <span className="mb-1.5 text-xs text-slate-500">week ending {rate.date}</span>}
        </div>
      </Card>

      <Card className="mb-5 p-6">
        <h3 className="mb-4 font-display font-bold text-slate-900">Outreach</h3>
        <Toggle k="sms_enabled" label="Texting (Ava + MLO + admin)" desc="Turn on only after your Retell numbers are approved for business texting (A2P 10DLC). Enables the Text button on leads, texts in Contact all, and appointment text reminders." />
        <Toggle k="deity_texting" label="AI Autopilot Mode texting" desc="When Texting is on, Ava follows up by text (once a day) when an AI Autopilot Mode call isn't answered." />
        <div className="mb-4 max-w-xs"><Label>Data refresh price per lead ($)</Label><Input type="number" step="0.01" min="0" className="mt-1.5" value={s.data_refresh_price_per_lead ?? 0.25} onChange={(e) => set("data_refresh_price_per_lead", e.target.value)} data-testid="setting-refresh-price" /><p className="mt-1 text-xs text-slate-400">Charged to the MLO for each lead the paid "Refresh missing data" button looks up (minimum $0.50 per refresh).</p></div>
        <Toggle k="email_outreach_enabled" label="Email outreach" desc="Allow prospect emails to be composed and sent." />
        <Toggle k="auto_email_new_leads" label="Auto-email new leads" desc="Automatically send an email to every eligible lead as soon as a run finishes." />
        <Toggle k="lead_release_enabled" label="Auto-release un-worked leads" desc="If an MLO doesn't personally contact a prospect within the window below, the lead is released so another MLO can claim it." />
        <div className="flex items-center justify-between gap-4 py-3">
          <div><div className="text-sm font-semibold text-slate-800">Release window (days)</div><div className="text-xs text-slate-500">Days without personal contact before a lead is auto-released.</div></div>
          <Input type="number" min="1" className="w-24" value={s.lead_release_days ?? 7} onChange={(e) => set("lead_release_days", e.target.value)} data-testid="setting-release-days" />
        </div>
        <Toggle k="cc_mlo_on_emails" label="CC MLO on prospect emails" desc="Assigned MLO is CC'd on every prospect email." />
      </Card>

      <TestWelcomeEmailButton />

      <Card className="mb-5 p-6">
        <h3 className="mb-1 font-display font-bold text-slate-900">Account Access</h3>
        <p className="mb-3 text-xs text-slate-500">Control how new Mortgage Loan Originators join.</p>
        <p className="mb-3 text-sm text-slate-600" data-testid="signup-approval-note">Every new sign-up starts as pending and can't sign in until you approve it in MLO Accounts. You get an email alert for each request.</p>
        <div className="mt-3">
          <Label>Public app URL <span className="text-xs font-normal text-slate-400">(used in welcome &amp; reset emails)</span></Label>
          <Input className="mt-1.5" value={s.app_url || ""} onChange={(e) => set("app_url", e.target.value)} placeholder="https://yourdomain.com" data-testid="setting-app-url" />
        </div>
      </Card>

      <DncBypassCard />

      <AttomTestCard />

      <KeyHealthCard />

      <Card className="mb-5 p-6">
        <h3 className="mb-1 font-display font-bold text-slate-900">Compliance Checks</h3>
        <p className="mb-3 text-xs text-slate-500">Rules enforced before automated voicemail outreach. The software enforces the rules you set; it does not independently determine legal compliance.</p>
        {[
          ["dnc_status", "Do Not Contact status"], ["consent_status", "Consent status"],
          ["phone_validation", "Phone validation"], ["outreach_eligibility", "Outreach eligibility"],
          ["jurisdiction", "Jurisdiction"], ["manual_approval", "Manual approval required"],
          ["suppression_status", "Suppression status"],
        ].map(([k, label]) => (
          <div key={k} className="flex items-center justify-between border-b border-slate-100 py-2.5">
            <span className="text-sm text-slate-700">{label}</span>
            <Switch checked={!!s.compliance_checks?.[k]} onCheckedChange={(v) => setCheck(k, v)} data-testid={`check-${k}`} />
          </div>
        ))}
      </Card>

      <Card className="mb-5 p-6">
        <h3 className="mb-4 font-display font-bold text-slate-900">Geographic Areas</h3>
        <Label>Available states (comma-separated)</Label>
        <Input className="mt-1.5" defaultValue={(s.available_states || []).join(", ")} onChange={(e) => set("available_states", e.target.value)} data-testid="setting-states" />
        <Label className="mt-4 block">Prospect qualification criteria (notes for the research agent)</Label>
        <Textarea className="mt-1.5" rows={3} value={s.qualification_criteria || ""} onChange={(e) => set("qualification_criteria", e.target.value)} data-testid="setting-qualification" />
      </Card>
    </div>
  );
}
